import { cloneDeep, get } from 'lodash'

import { ApiModel } from '~/plugins/api/model'

// Keep ApiModel instances outside Vuex state to avoid
// "do not mutate vuex store state outside mutation handlers" errors.
// The API core mutates these internally (isLoading, pagination, etc.).
const apiModels = {
  conversationsApi: new ApiModel(),
  conversationApi: new ApiModel(),
  sendMessageApi: new ApiModel(),
  createConversationApi: new ApiModel(),
  deleteConversationApi: new ApiModel()
}

const initialState = () => {
  return {
    conversations: [],
    activeConversation: null
  }
}

export const state = () => cloneDeep(initialState())

export const getters = {
  getConversations: state => state.conversations,
  getActiveConversation: state => state.activeConversation,
  isSendingMessage: () => apiModels.sendMessageApi.isLoading,
  isLoadingConversations: () => apiModels.conversationsApi.isLoading,
  isLoadingConversation: () => apiModels.conversationApi.isLoading
}

export const mutations = {
  SET_CONVERSATIONS(state, conversations) {
    state.conversations = conversations
  },

  SET_ACTIVE_CONVERSATION(state, conversation) {
    state.activeConversation = conversation
  },

  ADD_MESSAGE(state, message) {
    if (state.activeConversation?.messages) {
      state.activeConversation.messages.push(message)
    }
  },

  UPDATE_CONVERSATION_IN_LIST(state, conversation) {
    const index = state.conversations.findIndex(c => c.id === conversation.id)

    if (index !== -1) {
      state.conversations.splice(index, 1, { ...state.conversations[index], ...conversation })
    } else {
      state.conversations.unshift(conversation)
    }
  },

  REMOVE_CONVERSATION(state, conversationId) {
    state.conversations = state.conversations.filter(c => c.id !== conversationId)

    if (state.activeConversation?.id === conversationId) {
      state.activeConversation = null
    }
  }
}

export const actions = {
  async fetchConversations({ commit }) {
    const result = await this.$api.aiSearch(apiModels.conversationsApi).getConversations()
    const data = get(result, 'response.data', [])

    commit('SET_CONVERSATIONS', data)
  },

  async fetchConversation({ commit }, conversationId) {
    const result = await this.$api.aiSearch(apiModels.conversationApi).getConversation(conversationId)
    const data = get(result, 'response.data')

    if (data) {
      commit('SET_ACTIVE_CONVERSATION', data)
    }
  },

  async createConversation({ commit }) {
    const result = await this.$api.aiSearch(apiModels.createConversationApi).createConversation()
    const data = get(result, 'response.data')

    if (data) {
      commit('SET_ACTIVE_CONVERSATION', { ...data, messages: [] })
      commit('UPDATE_CONVERSATION_IN_LIST', data)
      return data
    }
  },

  async deleteConversation({ commit }, conversationId) {
    await this.$api.aiSearch(apiModels.deleteConversationApi).deleteConversation(conversationId)
    commit('REMOVE_CONVERSATION', conversationId)
  },

  async sendMessage({ commit, state, rootState }, { conversationId, content }) {
    // Optimistically add user message
    const currentUser = rootState.auth.user

    commit('ADD_MESSAGE', {
      role: 'user',
      content,
      createdByUser: currentUser ? { id: currentUser.id, firstName: currentUser.firstName } : null,
      createdAt: new Date().toISOString()
    })

    try {
      const result = await this.$api.aiSearch(apiModels.sendMessageApi).sendMessage(conversationId, content)
      const data = get(result, 'response.data')

      if (data) {
        // Add assistant response
        commit('ADD_MESSAGE', {
          role: 'assistant',
          content: data.content,
          sqlQuery: data.sqlQuery,
          queryResult: data.queryResult,
          createdAt: new Date().toISOString()
        })

        // Update conversation in sidebar list
        if (state.activeConversation) {
          commit('UPDATE_CONVERSATION_IN_LIST', cloneDeep(state.activeConversation))
        }
      }

      return result
    } catch {
      commit('ADD_MESSAGE', {
        role: 'assistant',
        content: 'Something went wrong. Please try again.',
        createdAt: new Date().toISOString()
      })
    }
  }
}
